import { toBytes } from "./bytes.js";
import { normalizeBaseUrl, strip0x } from "./internal/normalize.js";
// ------------------------------
// Fetch retry (read-only contract calls)
// ------------------------------
function normalizeContractIdForUrl(contractId) {
    return strip0x(String(contractId || "").trim());
}
function shouldRetryWithFeeder(text) {
    const t = String(text || "");
    // Nodes have historically returned error strings like "missing feed".
    // Some render it with spacing... keep it lenient.
    return /missing\s+feed/i.test(t) || /M\s*i\s*s\s*s\s*i\s*n\s*g\s+f\s*e\s*e\s*d/i.test(t);
}
// RUES is the node event-streaming protocol.
// We keep a tiny, dependency-free implementation here.
//
// Reference implementation: @dusk/w3sper (Deno + Browsers)
const RUES_VERSION = "1.0.0-rc.0";
const RUES_KEEP_ALIVE_MS = 30_000;
function toWsUrl(baseUrl) {
    const u = new URL(baseUrl);
    u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
    u.pathname = "/on";
    u.search = "";
    u.hash = "";
    return u.toString();
}
function parseRuesFrame(data) {
    // RUES frame format:
    // [u32 LE headers_len][headers_json_bytes][body_bytes]
    const view = new DataView(data);
    if (view.byteLength < 4)
        throw new Error("Invalid RUES frame");
    const headersLen = view.getUint32(0, true);
    if (4 + headersLen > view.byteLength)
        throw new Error("Invalid RUES headers length");
    const headersBytes = new Uint8Array(data, 4, headersLen);
    let headerPairs = [];
    try {
        headerPairs = JSON.parse(new TextDecoder().decode(headersBytes));
    }
    catch {
        headerPairs = [];
    }
    const headers = new Headers(headerPairs);
    const body = new Uint8Array(data, 4 + headersLen);
    const ct = String(headers.get("content-type") || "");
    // Try to decode JSON payloads, otherwise return bytes.
    if (/json/i.test(ct)) {
        try {
            return { headers, payload: JSON.parse(new TextDecoder().decode(body)) };
        }
        catch {
            // fall through
        }
    }
    return { headers, payload: body };
}
async function subscribeRues(f, url, sessionId, signal) {
    const headers = new Headers();
    headers.set("rusk-version", RUES_VERSION);
    headers.set("rusk-session-id", sessionId);
    const init = { method: "GET", headers };
    if (signal)
        init.signal = signal;
    const res = await f(url, init);
    if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Unable to subscribe (${res.status} ${res.statusText})`);
    }
    // Subscription is established server-side; we don't need the body.
    try {
        await res.body?.cancel();
    }
    catch {
        // ignore
    }
}
/** Create a lightweight Rusk HTTP/RUES client for contract reads and tx waits. */
export function createDuskNodeClient(opts) {
    const f = opts.fetch ?? fetch;
    const getBaseUrl = () => {
        const u = typeof opts.baseUrl === "function" ? opts.baseUrl() : opts.baseUrl;
        return normalizeBaseUrl(u);
    };
    const contractCall = async (contractId, fnName, body, callOpts = {}) => {
        const base = getBaseUrl();
        if (!base) {
            throw new Error("DuskNodeClient: baseUrl is empty");
        }
        const cid = normalizeContractIdForUrl(contractId);
        const url = `${base}/on/contracts:${cid}/${String(fnName)}`;
        const headers = new Headers();
        headers.set("Content-Type", "application/octet-stream");
        // Be explicit: the SDK expects raw RKYV bytes back.
        headers.set("Accept", "application/octet-stream");
        const bodyBytes = toBytes(body);
        // Some TS lib combinations (DOM + older Node fetch typings) don't accept Uint8Array as
        // `RequestInit.body` even though it works at runtime. Normalize to an ArrayBuffer slice.
        const _buf = bodyBytes.buffer;
        const bodyArrayBuffer = _buf.slice(bodyBytes.byteOffset, bodyBytes.byteOffset + bodyBytes.byteLength);
        const doFetch = async (feeder) => {
            if (feeder)
                headers.set("Rusk-feeder", "true");
            else
                headers.delete("Rusk-feeder");
            const init = {
                method: "POST",
                headers,
                body: bodyArrayBuffer,
            };
            if (callOpts.signal)
                init.signal = callOpts.signal;
            // Retry a couple of times on transient gateway errors (testnet gateways can be flaky).
            const retryable = [429, 502, 503, 504];
            for (let i = 0; i < 3; i++) {
                try {
                    const res = await f(url, init);
                    if (res.ok || i === 2 || !retryable.includes(res.status))
                        return res;
                    try {
                        res.body?.cancel?.();
                    }
                    catch { /* ignore */ }
                }
                catch (e) {
                    // Abort should not retry.
                    if (callOpts.signal?.aborted)
                        throw e;
                    if (i === 2)
                        throw e;
                }
                await new Promise((r) => setTimeout(r, 250 * 2 ** i));
            }
            // Should never get here.
            return await f(url, init);
        };
        // Respect explicit feeder choice.
        if (callOpts.feeder === true) {
            const res = await doFetch(true);
            if (!res.ok)
                throw new Error((await res.text()) || `HTTP ${res.status}`);
            return new Uint8Array(await res.arrayBuffer());
        }
        if (callOpts.feeder === false) {
            const res = await doFetch(false);
            if (!res.ok)
                throw new Error((await res.text()) || `HTTP ${res.status}`);
            return new Uint8Array(await res.arrayBuffer());
        }
        // Auto fallback: try non-feeder first.
        let res = await doFetch(false);
        if (!res.ok) {
            const txt = await res.text();
            if (shouldRetryWithFeeder(txt)) {
                res = await doFetch(true);
                if (!res.ok)
                    throw new Error((await res.text()) || `HTTP ${res.status}`);
            }
            else {
                throw new Error(txt || `HTTP ${res.status}`);
            }
        }
        return new Uint8Array(await res.arrayBuffer());
    };
    const waitForTxExecuted = async (hash, waitOpts = {}) => {
        const base = getBaseUrl();
        if (!base)
            throw new Error("DuskNodeClient: baseUrl is empty");
        const tx = strip0x(String(hash || "").trim()).toLowerCase();
        if (!tx)
            return null;
        if (typeof WebSocket === "undefined") {
            throw new Error("waitForTxExecuted requires WebSocket support (browser environment)");
        }
        const timeoutMs = Number(waitOpts.timeoutMs ?? 60_000);
        const signal = waitOpts.signal;
        const wsUrl = toWsUrl(base);
        const topicUrl = `${base}/on/transactions:${tx}/Executed`;
        // Best-effort event-driven wait: open a fresh session, subscribe to the tx topic,
        // then resolve on the first matching event.
        return await new Promise((resolve, reject) => {
            let finished = false;
            let sessionId = null;
            let subscribed = false;
            let keepAliveId;
            let timeoutId;
            const ws = new WebSocket(wsUrl);
            ws.binaryType = "arraybuffer";
            const cleanup = () => {
                if (finished)
                    return;
                finished = true;
                if (timeoutId)
                    clearTimeout(timeoutId);
                if (keepAliveId)
                    clearInterval(keepAliveId);
                try {
                    ws.close();
                }
                catch {
                    // ignore
                }
                if (signal)
                    signal.removeEventListener("abort", onAbort);
            };
            const fail = (err) => {
                cleanup();
                reject(err instanceof Error ? err : new Error(String(err)));
            };
            const onAbort = () => {
                fail(signal?.reason ?? new Error("Aborted"));
            };
            if (signal) {
                if (signal.aborted) {
                    onAbort();
                    return;
                }
                signal.addEventListener("abort", onAbort, { once: true });
            }
            timeoutId = setTimeout(() => {
                cleanup();
                resolve(null);
            }, timeoutMs);
            ws.addEventListener("error", () => fail(new Error("RUES websocket error")));
            ws.addEventListener("close", () => {
                // If we already resolved/rejected, ignore.
                if (finished)
                    return;
                fail(new Error("RUES websocket closed"));
            });
            ws.addEventListener("message", async (ev) => {
                try {
                    // The first ws message is the session id (string) in RUES.
                    if (!sessionId) {
                        sessionId = typeof ev.data === "string" ? ev.data : String(ev.data);
                        keepAliveId = setInterval(() => {
                            try {
                                if (ws.readyState === WebSocket.OPEN)
                                    ws.send("");
                            }
                            catch {
                                // ignore
                            }
                        }, RUES_KEEP_ALIVE_MS);
                        await subscribeRues(f, topicUrl, sessionId, signal);
                        subscribed = true;
                        return;
                    }
                    if (!subscribed)
                        return;
                    if (!(ev.data instanceof ArrayBuffer))
                        return;
                    const { headers, payload } = parseRuesFrame(ev.data);
                    const loc = String(headers.get("content-location") || "").toLowerCase();
                    // When subscribed to `/transactions:<id>/Executed` we *should* only get this tx,
                    // but keep a small guard so we don't resolve on unrelated frames.
                    if (!loc || !loc.includes(tx))
                        return;
                    cleanup();
                    resolve({ headers, payload });
                }
                catch (e) {
                    fail(e);
                }
            });
        });
    };
    return { getBaseUrl, contractCall, waitForTxExecuted };
}
//# sourceMappingURL=node.js.map